import { getBuild } from './api';
import { isCordova } from './mobile/deviceDetection';

/**
 * Detects that the page booted an outdated frontend build (pre-rendered S3 HTML
 * can reference an old `/builds/<id>/`) and reloads once with `?_v=<serverBuild>`.
 * A request with a query string bypasses the static file and is rendered by SSR
 * with the current build; the server also uses `_v` to refresh the stale file.
 */

const STALE_RELOAD_PARAM = '_v';
const BOT_UA_PATTERN =
  /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|headless|lighthouse|pagespeed/i;
const BUILD_ID_PATTERN = /\/builds\/([^/?#]+)/;
const BUILD_VERSION_PATTERN = /^(\d+)\.(\d+)\.(\d+)-(.+)$/;
const STRIP_FALLBACK_DELAY_MS = 2000;

/**
 * @param {string} [publicPath] e.g. `/builds/1.102.1-3a5dc61` (`process.env.PUBLIC_URL`)
 * @returns {string | null} e.g. `1.102.1-3a5dc61`
 */
export const buildIdFromPublicPath = (publicPath) => {
  if (typeof publicPath !== 'string') return null;
  const match = BUILD_ID_PATTERN.exec(publicPath);
  return match ? match[1] : null;
};

const parseBuildId = (build) => {
  const match = typeof build === 'string' ? BUILD_VERSION_PATTERN.exec(build) : null;
  return match ? { semver: [+match[1], +match[2], +match[3]], sha: match[4] } : null;
};

const compareSemver = (a, b) => a.findIndex((n, i) => n !== b[i]);

/**
 * @param {string | null} serverBuild
 * @param {string | null} clientBuild
 * @returns {boolean} true when the server build is a higher semver, or the same
 *   semver with a different sha; false when equal, older or unparsable
 */
export const isNewerBuild = (serverBuild, clientBuild) => {
  const server = parseBuildId(serverBuild);
  const client = parseBuildId(clientBuild);
  if (!server || !client) return false;
  const diffAt = compareSemver(server.semver, client.semver);
  if (diffAt === -1) return server.sha !== client.sha;
  return server.semver[diffAt] > client.semver[diffAt];
};

/**
 * @param {Window} win
 * @returns {boolean}
 */
export const isBotEnvironment = (win) => {
  const nav = win.navigator || {};
  return nav.webdriver === true || BOT_UA_PATTERN.test(nav.userAgent || '');
};

/**
 * @param {string} href
 * @param {string} build
 * @returns {string} href with `_v=<build>` set; other params and the hash are kept
 */
export const withStaleReloadParam = (href, build) => {
  const url = new URL(href);
  url.searchParams.set(STALE_RELOAD_PARAM, build);
  return url.toString();
};

/**
 * @param {string} href
 * @returns {string} href without `_v`; other params and the hash are kept
 */
export const withoutStaleReloadParam = (href) => {
  const url = new URL(href);
  url.searchParams.delete(STALE_RELOAD_PARAM);
  return url.toString();
};

const hasStaleReloadParam = (href) => new URL(href).searchParams.has(STALE_RELOAD_PARAM);

const scheduleIdle = (win, fn) => {
  if (typeof win.requestIdleCallback === 'function') {
    win.requestIdleCallback(fn);
  } else {
    win.setTimeout(fn, STRIP_FALLBACK_DELAY_MS);
  }
};

const stripReloadParamWhenIdle = (win) => {
  scheduleIdle(win, () => {
    win.history.replaceState(win.history.state, '', withoutStaleReloadParam(win.location.href));
  });
};

const reloadIfOutdated = async (win, fetchBuild, clientBuild) => {
  const response = await fetchBuild();
  const { build: serverBuild, enforce } = response?.data || {};
  // enforce is false right after a deploy, while the static run still refreshes the pages.
  if (enforce === true && isNewerBuild(serverBuild, clientBuild)) {
    win.location.replace(withStaleReloadParam(win.location.href, serverBuild));
  }
};

const defaultWindow = () => (typeof window === 'undefined' ? undefined : window);

const shouldSkip = (win, clientBuild) =>
  !win || isCordova() || !clientBuild || isBotEnvironment(win);

/**
 * Reloads once with `?_v=<serverBuild>` when the server renders a newer build (and enforces it)
 * than the one this page booted. Never throws and fails open: network errors,
 * timeouts, a `null` build or an existing `_v` (loop guard) do nothing.
 *
 * @param {{ fetchBuild?: () => Promise<{ data?: { build?: string | null } }>,
 *   win?: Window, clientBuild?: string | null }} [options]
 * @returns {Promise<void>}
 */
export const checkStaleBuild = async ({
  fetchBuild = getBuild,
  win = defaultWindow(),
  clientBuild = buildIdFromPublicPath(process.env.PUBLIC_URL),
} = {}) => {
  try {
    if (shouldSkip(win, clientBuild)) return;
    if (hasStaleReloadParam(win.location.href)) {
      stripReloadParamWhenIdle(win);
      return;
    }
    await reloadIfOutdated(win, fetchBuild, clientBuild);
  } catch (e) {
    // Fail open: a failed check must never affect the page.
  }
};
